import { useState } from "react";
import { Search, Monitor, Laptop, ChevronDown, ChevronRight, Plug, MessageSquare, Terminal, FolderOpen, Users, Wifi, RefreshCw, LifeBuoy, X, Info, Clock, FileText, Activity, Download, Settings, AlertTriangle, ShieldCheck, Trash2, PanelLeftClose, PanelLeftOpen, MoreHorizontal, Pencil, ArrowUp, ArrowDown, ArrowUpDown, Copy, Power, RotateCcw, StickyNote, Wrench } from "lucide-react";
import { toast } from "sonner";
import { DConnections, DChat, DCommands, DNotes } from "./preview/DLive";
import { LogoMark } from "@/components/AuthShell";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "@/components/ui/context-menu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const PCS = [
  { n: "PC-ACCUEIL", c: "Cabinet Martin", u: "Sophie", on: true, os: "win", old: false, sess: true },
  { n: "PC-COMPTA-02", c: "Cabinet Martin", u: "Julien", on: true, os: "win", old: true },
  { n: "MAC-DIRECTION", c: "Cabinet Martin", u: "Claire", on: false, os: "mac", old: false },
  { n: "POSTE-ATELIER", c: "Garage Durand", u: "Marc", on: true, os: "win", old: false },
  { n: "PC-BUREAU", c: "Garage Durand", u: "Inès", on: false, os: "win", old: true },
  { n: "PORTABLE-COMMERCIAL", c: "Agence Horizon", u: "Thomas", on: true, os: "win", old: false, help: true },
];
const GROUPS = [
  { l: "Toutes les machines", n: 6, i: Monitor }, { l: "En ligne", n: 4, i: Wifi }, { l: "À mettre à jour", n: 2, i: RefreshCw },
  { l: "Demandes d'aide", n: 1, i: LifeBuoy }, { l: "Cabinet Martin", n: 3, i: FolderOpen }, { l: "Garage Durand", n: 2, i: FolderOpen }, { l: "Agence Horizon", n: 1, i: FolderOpen },
];
const Dot = ({ on, className = "" }) => <span className={`inline-block h-2 w-2 rounded-full ${on ? "bg-emerald-500" : "bg-slate-300"} ${className}`} />;
const Red = ({ old }) => old && <span className="inline-block h-2 w-2 rounded-full bg-red-500 mr-1.5" title="Agent à mettre à jour" />;
const Os = ({ os }) => (os === "mac" ? <Laptop className="h-4 w-4 text-slate-400" /> : <Monitor className="h-4 w-4 text-slate-400" />);
const Search1 = ({ dark }) => (
  <div className={`flex items-center gap-2 rounded-md px-3 h-9 text-sm ${dark ? "bg-white/10 text-white/60" : "bg-white border border-slate-200 text-slate-400"}`}><Search className="h-4 w-4" />Rechercher…</div>
);
const Row = ({ p, sel, dense }) => (
  <div className={`px-4 ${dense ? "py-1.5" : "py-2.5"} border-b border-slate-200 flex items-start gap-3 ${sel ? "bg-sky-50 border-l-4 border-l-sky-600" : "border-l-4 border-l-transparent"}`}>
    <Os os={p.os} />
    <div className="flex-1 min-w-0 text-[13px] leading-5">
      <p className="font-semibold text-slate-900 flex items-center"><Red old={p.old} />{p.n}{p.help && <span className="ml-2 text-[10px] bg-orange-500 text-white rounded px-1.5">Aide</span>}</p>
      {!dense && <p className="text-slate-500">Entreprise : {p.c}</p>}
      <p className="text-slate-500">Utilisateur : {p.u}{!p.on && " (hors ligne)"}</p>
    </div>
    <Dot on={p.on} className="mt-1.5" />
  </div>
);
const Detail = ({ horizontal }) => (
  <div className={`bg-white ${horizontal ? "flex gap-8 items-start p-5" : "p-5 space-y-4"}`}>
    <div>
      <p className="text-xs text-slate-500">Poste sélectionné</p>
      <p className="text-lg font-bold text-slate-900 flex items-center gap-2"><Dot on />PC-ACCUEIL</p>
      <p className="text-sm text-slate-500">Cabinet Martin · Sophie · Windows 11</p>
    </div>
    <div className="flex gap-2">
      <span className="rounded-md bg-sky-600 text-white text-sm font-semibold px-4 h-9 whitespace-nowrap flex items-center gap-1.5"><Plug className="h-4 w-4" />Se connecter</span>
      <span className="rounded-md border border-slate-200 text-sm px-3 h-9 flex items-center gap-1.5"><MessageSquare className="h-4 w-4" />Message</span>
      <span className="rounded-md border border-slate-200 text-sm px-3 h-9 flex items-center gap-1.5"><Terminal className="h-4 w-4" />Commande</span>
    </div>
    <dl className={`text-sm grid ${horizontal ? "grid-cols-3 gap-x-8 gap-y-1" : "grid-cols-[110px_1fr] gap-y-1"}`}>
      {[["Système", "Windows 11 Pro"], ["Adresse IP", "192.168.1.24"], ["Agent", "1.6.7"], ["Inactif", "2 min"], ["Processeur", "12 %"], ["Disque", "41 %"]].map(([k, v]) => (
        <div key={k} className="contents"><dt className="text-slate-500">{k}</dt><dd className="text-slate-900">{v}</dd></div>
      ))}
    </dl>
  </div>
);
const GroupList = ({ dark, compact }) => (
  <div className="py-2">
    {GROUPS.map((g, i) => (
      <div key={g.l} className={`flex items-center gap-2.5 px-4 ${compact ? "py-1.5" : "py-2"} text-[13px] ${i === 0 ? (dark ? "bg-white/10 text-white font-semibold" : "bg-sky-100 text-sky-900 font-semibold") : dark ? "text-white/70" : "text-slate-700"}`}>
        <g.i className="h-4 w-4 opacity-70" /><span className="flex-1">{g.l}</span>
        <span className={`text-[11px] rounded-full px-2 ${dark ? "bg-white/10" : "bg-slate-200"}`}>{g.n}</span>
      </div>
    ))}
  </div>
);
const Frame = ({ children }) => <div className="h-[640px] text-slate-900 bg-slate-50 font-[Manrope,sans-serif]">{children}</div>;

// D — dark sidebar (views + clients), dense list with toolbar, detail card with screen preview, tabs and health.
const DSection = ({ title, items: all, active, mini, foldable }) => {
  const [folded, setFolded] = useState(false);
  const items = foldable && folded ? all.slice(0, 1) : all;
  return (
  <div className="mt-3">
    {mini ? <div className="mx-3 mb-1 border-t border-white/10" /> : <p className="px-4 pb-1 text-[10px] uppercase tracking-[0.14em] text-white/40">{title}</p>}
    {items.map((g, idx) => mini ? (
      <div key={g.l} title={g.l} className={`relative mx-auto my-0.5 h-9 w-9 rounded-md flex items-center justify-center ${g.l === active ? "bg-sky-500/20 text-sky-300" : "text-white/70 hover:bg-white/5"}`}>
        <g.i className="h-[18px] w-[18px]" />
        {g.n !== undefined && g.n > 0 && g.warn && title === "Vues" && <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full text-[9px] font-bold flex items-center justify-center px-1 ${g.warn ? "bg-red-500 text-white" : "bg-sky-500 text-white"}`}>{g.n}</span>}
      </div>
    ) : (
      <div key={g.l} onClick={foldable && idx === 0 ? () => setFolded((f) => !f) : undefined} data-testid={foldable && idx === 0 ? `d-fold-${title}` : undefined}
        className={`mx-2 rounded-md flex items-center gap-2.5 px-3 py-1.5 text-[13px] ${foldable && idx === 0 ? "cursor-pointer select-none" : ""} ${g.l === active ? "bg-sky-500/20 text-white font-semibold" : "text-white/70"}`}>
        <g.i className={`h-4 w-4 ${g.l === active ? "text-sky-300" : "opacity-60"}`} /><span className="flex-1 truncate">{g.l}</span>
        {foldable && idx === 0 && (folded ? <ChevronRight className="h-3.5 w-3.5 opacity-60" /> : <ChevronDown className="h-3.5 w-3.5 opacity-60" />)}
        {g.n !== undefined && <span className={`text-[11px] rounded-full px-2 ${g.warn ? "bg-red-500/80 text-white" : "bg-white/10"}`}>{g.n}</span>}
      </div>
    ))}
  </div>
  );
};
const MACHINE_ACTIONS = (p) => [["connect", Plug, "Se connecter", !p.on], ["edit", Pencil, "Modifier"], ["note", StickyNote, "Ajouter une note"], "-",
  ["message", MessageSquare, "Envoyer un message", !p.on], ["command", Terminal, "Exécuter une commande", !p.on], ["toolbox", Wrench, "Boîte à commandes", !p.on],
  ["restart", RotateCcw, "Redémarrer", !p.on], ["wake", Power, "Réveiller (Wake-on-LAN)", p.on], "-",
  ["history", Clock, "Voir les connexions"], ["remove", Trash2, "Supprimer de la liste", false, true]];
const DRow = ({ p, sel, onPick, act }) => (
  <ContextMenu>
  <ContextMenuTrigger asChild>
  <div onClick={onPick} onContextMenu={onPick} data-testid={`d-row-${p.n}`} className={`group cursor-pointer px-3 py-2 border-b border-slate-100 flex items-center gap-3 ${sel ? "bg-sky-50 shadow-[inset_3px_0_0_#0284c7]" : "hover:bg-slate-50"}`}>
    <input type="checkbox" readOnly checked={sel} className="h-3.5 w-3.5 accent-sky-600" />
    <div className={`h-8 w-8 rounded-md flex items-center justify-center ${p.on ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
      {p.os === "mac" ? <Laptop className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
    </div>
    <div className="flex-1 min-w-0 text-[12.5px] leading-[18px]">
      <p className="font-semibold text-slate-900 flex items-center truncate"><Red old={p.old} />{p.n}
        {p.help && <span className="ml-2 text-[10px] bg-orange-500 text-white rounded px-1.5">Aide</span>}
        {p.sess && <span className="ml-2 text-[10px] bg-sky-600 text-white rounded px-1.5">En session</span>}</p>
      <p className="text-slate-500 truncate">{p.c}</p>
      <p className="text-slate-500 truncate">{p.u}{p.on ? "" : " · hors ligne"}</p>
    </div>
    <span className="hidden group-hover:flex gap-1">
      {p.on
        ? <><span title="Se connecter" className="h-7 w-7 rounded-md bg-sky-600 text-white flex items-center justify-center"><Plug className="h-3.5 w-3.5" /></span>
          <span title="Message" onClick={(e) => { e.stopPropagation(); onPick(); act("message", p); }} className="h-7 w-7 rounded-md border border-slate-200 flex items-center justify-center"><MessageSquare className="h-3.5 w-3.5" /></span></>
        : <span title="Réveiller (Wake-on-LAN)" onClick={(e) => { e.stopPropagation(); act("wake", p); }} data-testid={`d-row-wake-${p.n}`} className="h-7 w-7 rounded-md border border-slate-200 flex items-center justify-center"><Power className="h-3.5 w-3.5" /></span>}
    </span>
    <Dot on={p.on} className="group-hover:hidden" />
  </div>
  </ContextMenuTrigger>
  <ContextMenuContent className="w-64" data-testid="d-machine-menu">
    {MACHINE_ACTIONS(p).map((a, k) => {
      if (a === "-") return <ContextMenuSeparator key={k} />;
      const [id, Icon, label, off, danger] = a;
      return <ContextMenuItem key={id} disabled={off} onSelect={() => act(id, p)} data-testid={`d-ctx-${id}`} className={danger ? "text-red-600 focus:text-red-700" : ""}><Icon className="h-4 w-4 mr-2" />{label}</ContextMenuItem>;
    })}
  </ContextMenuContent>
  </ContextMenu>
);
const Gauge = ({ l, v }) => (
  <div><div className="flex justify-between text-[11px] text-slate-500"><span>{l}</span><span className="font-semibold text-slate-800">{v} %</span></div>
    <div className="h-1.5 rounded bg-slate-100 mt-1"><div className={`h-full rounded ${v > 80 ? "bg-red-500" : "bg-sky-500"}`} style={{ width: `${v}%` }} /></div></div>
);
const NAV = [{ l: "Access", i: Monitor }, { l: "Support", i: LifeBuoy, n: 2 }, { l: "RMM", i: Activity, n: 3, warn: true }, { l: "Déployer", i: Download }];
const DTABS = [["Infos"], ["Santé", 1], ["Mises à jour", 4], ["Logiciels"], ["Connexions"], ["Messages"], ["Commandes"], ["Notes"]];
const Btn = ({ children, primary }) => <span className={`rounded-md text-[13px] font-semibold px-3 h-8 inline-flex items-center gap-1.5 whitespace-nowrap ${primary ? "bg-sky-600 text-white" : "border border-slate-200 text-slate-700"}`}>{children}</span>;
const DHealth = () => (
  <div className="p-4 space-y-3 text-[13px]">
    <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /><b>Disque D: presque plein</b><span className="text-slate-600">· 91 % utilisé · depuis 2 h</span><span className="ml-auto"><Btn>Résoudre</Btn></span></div>
    <div className="grid grid-cols-3 gap-3">
      {[["Antivirus", "Defender à jour", true], ["Pare-feu", "Activé", true], ["Redémarrage", "En attente", false], ["Sauvegarde", "Il y a 1 j", true], ["Démarré", "il y a 3 j", true], ["Température", "48 °C", true]].map(([k, v, ok]) => (
        <div key={k} className="rounded-lg border border-slate-200 p-2.5"><p className="text-[11px] text-slate-500">{k}</p><p className={`font-semibold flex items-center gap-1.5 ${ok ? "text-slate-900" : "text-amber-700"}`}>{ok ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4" />}{v}</p></div>
      ))}
    </div>
  </div>
);
const DUpdates = () => (
  <div className="p-4 text-[13px]">
    <div className="flex items-center gap-2 mb-2"><b>4 mises à jour Windows disponibles</b><span className="text-slate-500">· vérifié il y a 12 min</span><span className="ml-auto flex gap-2"><Btn>Vérifier</Btn><Btn primary><Download className="h-4 w-4" />Installer (3)</Btn></span></div>
    {[["Mise à jour cumulative Windows 11 (KB5044284)", "Sécurité", true], ["Microsoft Defender – définitions 1.419.12", "Antivirus", true], ["Pilote Intel Graphics 31.0.101", "Pilote", false], ["Microsoft Edge 130.0.2849", "Logiciel", true]].map(([n, k, c]) => (
      <label key={n} className="flex items-center gap-2 py-1.5 border-b border-slate-100"><input type="checkbox" readOnly checked={c} className="accent-sky-600" /><span className="flex-1">{n}</span><span className="text-[11px] bg-slate-100 rounded px-1.5">{k}</span></label>
    ))}
    <label className="flex items-center gap-2 mt-2 text-slate-600"><input type="checkbox" readOnly checked className="accent-sky-600" />Redémarrer automatiquement hors heures de travail (après 19 h)</label>
  </div>
);
const DSoftware = () => (
  <div className="p-4 text-[13px] grid grid-cols-2 gap-5">
    <div>
      <p className="font-semibold mb-1">Installer depuis le catalogue</p>
      <div className="flex items-center gap-2 rounded-md border border-slate-200 px-2 h-8 text-slate-400 mb-2"><Search className="h-4 w-4" />Chrome, 7-Zip, VLC…</div>
      <div className="flex flex-wrap gap-1.5">{["Google Chrome", "7-Zip", "VLC", "Adobe Reader", "Zoom", "Thunderbird"].map((x, i) => <span key={x} className={`rounded-full px-2.5 py-1 text-[12px] ${i < 2 ? "bg-sky-600 text-white" : "bg-slate-100"}`}>{x}</span>)}</div>
      <div className="mt-3"><Btn primary><Download className="h-4 w-4" />Installer 2 logiciels</Btn></div>
    </div>
    <div>
      <p className="font-semibold mb-1">Installés (86)</p>
      {[["Microsoft Office 365", "16.0.18129", false], ["Mozilla Firefox", "131.0.3", true], ["WinRAR", "6.24", true], ["TeamViewer", "15.58", false]].map(([n, v, up]) => (
        <div key={n} className="flex items-center gap-2 py-1.5 border-b border-slate-100"><input type="checkbox" readOnly className="accent-sky-600" /><span className="flex-1">{n}</span><span className="text-[11px] text-slate-500">{v}</span>{up && <span className="text-[10px] bg-amber-100 text-amber-800 rounded px-1.5">MAJ</span>}</div>
      ))}
      <div className="mt-2 flex gap-2"><Btn>Mettre à jour (2)</Btn><Btn><Trash2 className="h-4 w-4" />Désinstaller</Btn></div>
    </div>
  </div>
);
// Clients: same actions as ScreenConnect groups (modify, delete, move to position / up / down, clone), via right-click or "•••".
const CLIENT_ACTIONS = (i, n) => [
  ["edit", Pencil, "Modifier"], ["delete", Trash2, "Supprimer", true], "-",
  ["pos", ArrowUpDown, "Déplacer vers la position…"], ["up", ArrowUp, "Déplacer vers le haut", i === 0], ["down", ArrowDown, "Déplacer vers le bas", i === n - 1], "-",
  ["clone", Copy, "Cloner"],
];
const ClientMenuItems = ({ Item, Sep, i, n, run }) => CLIENT_ACTIONS(i, n).map((a, k) => {
  if (a === "-") return <Sep key={k} />;
  const [id, Icon, label, off] = a;
  return (
    <Item key={id} disabled={id !== "delete" && off} onSelect={() => run(id)} data-testid={`d-client-${i}-${id}`}
      className={id === "delete" ? "text-red-600 focus:text-red-700" : ""}><Icon className="h-4 w-4 mr-2" />{label}</Item>
  );
});
const DClients = ({ mini }) => {
  const [list, setList] = useState(GROUPS.slice(4).map((g) => ({ l: g.l, n: g.n })));
  const swap = (i, j) => setList((L) => { const c = [...L]; [c[i], c[j]] = [c[j], c[i]]; return c; });
  const run = (i) => (a) => {
    const g = list[i];
    if (a === "edit") { const v = window.prompt("Nouveau nom du client", g.l); if (v?.trim()) setList((L) => L.map((x, k) => (k === i ? { ...x, l: v.trim() } : x))); }
    if (a === "delete" && window.confirm(`Supprimer « ${g.l} » ? Ses postes passent dans « Non classé ».`)) setList((L) => L.filter((_, k) => k !== i));
    if (a === "up") swap(i, i - 1);
    if (a === "down") swap(i, i + 1);
    if (a === "pos") {
      const v = parseInt(window.prompt(`Position (1 à ${list.length})`, String(i + 1)), 10);
      if (v >= 1 && v <= list.length) setList((L) => { const c = L.filter((_, k) => k !== i); c.splice(v - 1, 0, g); return c; });
    }
    if (a === "clone") setList((L) => { const c = [...L]; c.splice(i + 1, 0, { l: `${g.l} (copie)`, n: 0 }); return c; });
  };
  return (
    <div className="mt-3" data-testid="d-clients">
      {mini ? <div className="mx-3 mb-1 border-t border-white/10" /> : <p className="px-4 pb-1 text-[10px] uppercase tracking-[0.14em] text-white/40">Clients</p>}
      {list.map((g, i) => (
        <ContextMenu key={g.l + i}>
          <ContextMenuTrigger asChild>
            {mini ? (
              <div title={g.l} className="relative mx-auto my-0.5 h-9 w-9 rounded-md flex items-center justify-center text-white/70 hover:bg-white/5">
                <FolderOpen className="h-[18px] w-[18px]" />
              </div>
            ) : (
              <div className="group mx-2 rounded-md flex items-center gap-2.5 px-3 py-1.5 text-[13px] text-white/70 hover:bg-white/5" data-testid={`d-client-${i}`}>
                <FolderOpen className="h-4 w-4 opacity-60" /><span className="flex-1 truncate">{g.l}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 h-5 w-5 rounded hover:bg-white/10 flex items-center justify-center" data-testid={`d-client-${i}-more`}><MoreHorizontal className="h-4 w-4" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-60">
                    <ClientMenuItems Item={DropdownMenuItem} Sep={DropdownMenuSeparator} i={i} n={list.length} run={run(i)} />
                  </DropdownMenuContent>
                </DropdownMenu>
                <span className="text-[11px] rounded-full px-2 bg-white/10">{g.n}</span>
              </div>
            )}
          </ContextMenuTrigger>
          <ContextMenuContent className="w-60">
            <ClientMenuItems Item={ContextMenuItem} Sep={ContextMenuSeparator} i={i} n={list.length} run={run(i)} />
          </ContextMenuContent>
        </ContextMenu>
      ))}
    </div>
  );
};
const INFO = [
  ["Session", [["Nom", "PC-ACCUEIL"], ["Entreprise", "Cabinet Martin"], ["Site", ""], ["Département", ""], ["Type d'appareil", "Windows"],
    ["Hôtes connectés", "Frédéric (25/10/2026 16:35)"], ["Invités connectés", "Sophie (25/10/2026 08:12)"],
    ["Utilisateur connecté", "CABINET\\sophie"], ["Temps d'inactivité", "2 min"]]],
  ["Appareil", [["Machine", "PC-ACCUEIL.cabinet.local"], ["Système d'exploitation", "Windows 11 Pro 24H2"], ["Processeur(s)", "Intel Core i5-12400"],
    ["Mémoire", "16 Go"], ["Écran", "1920 × 1080"], ["IP locale", "192.168.1.24"], ["IP publique", "82.67.105.234"], ["Allumé depuis", "3j 4h"],
    ["Version de l'agent", "1.6.6 / 1.6.7 (à mettre à jour)", "text-red-600 font-semibold"], ["Inscrit le", "19/10/2026 21:49"]]],
];
const SideDark = () => {
  const [tab, setTab] = useState("Infos");
  const [mini, setMini] = useState(false);
  const [selN, setSelN] = useState(PCS[0].n);
  const p = PCS.find((x) => x.n === selN);
  const act = (a, m) => {
    const TAB = { message: "Messages", command: "Commandes", toolbox: "Commandes", note: "Notes", history: "Connexions" };
    if (TAB[a]) return setTab(TAB[a]);
    if (a === "wake") return toast.success(`Signal de réveil envoyé à ${m.n} (Wake-on-LAN)`);
    if (a === "restart") return window.confirm(`Redémarrer ${m.n} maintenant ? L'utilisateur sera prévenu.`) && toast.success(`Redémarrage de ${m.n} demandé`);
    toast.info(`Aperçu : « ${a} » sur ${m.n} (identique à l'actuel)`);
  };
  return (
  <Frame>
    <div className={`grid h-full transition-[grid-template-columns] duration-200 ${mini ? "grid-cols-[64px_380px_1fr]" : "grid-cols-[260px_380px_1fr]"}`}>
      <div className="bg-[#0b1e3f] text-white flex flex-col min-w-0 overflow-hidden" data-testid="d-sidebar">
        <div className={`pt-4 pb-2 flex items-center gap-2 ${mini ? "justify-center" : "px-4"}`}>
          <LogoMark className="h-7 w-7 shrink-0" />{!mini && <span className="font-extrabold flex-1">ScreenView</span>}
          {!mini && <button onClick={() => setMini(true)} title="Réduire le menu" data-testid="d-collapse-btn" className="h-7 w-7 rounded-md hover:bg-white/10 flex items-center justify-center"><PanelLeftClose className="h-4 w-4" /></button>}
        </div>
        {mini
          ? <button onClick={() => setMini(false)} title="Agrandir le menu" data-testid="d-expand-btn" className="mx-auto h-9 w-9 rounded-md hover:bg-white/10 flex items-center justify-center text-white/70"><PanelLeftOpen className="h-[18px] w-[18px]" /></button>
          : <div className="px-3"><Search1 dark /></div>}
        <DSection mini={mini} foldable title="Espaces" active="Access" items={NAV} />
        <DSection mini={mini} foldable title="Vues" active="Toutes les machines" items={[GROUPS[0], GROUPS[1], { ...GROUPS[2], warn: true }, { ...GROUPS[3], warn: true }]} />
        <DClients mini={mini} />
        <div className="mt-auto">
          {!mini && <div className="mx-3 mb-2 rounded-lg bg-white/5 p-3 text-[12px] text-white/70">4 postes en ligne sur 6<div className="h-1.5 rounded bg-white/10 mt-2"><div className="h-full w-2/3 rounded bg-emerald-400" /></div></div>}
          <div className="border-t border-white/10 py-2" data-testid="d-admin-link">
            {mini
              ? <div title="Admin" className="mx-auto h-9 w-9 rounded-md flex items-center justify-center text-white/70 hover:bg-white/5"><Settings className="h-[18px] w-[18px]" /></div>
              : <div className="mx-2 rounded-md flex items-center gap-2.5 px-3 py-2 text-[13px] text-white/80 hover:bg-white/5"><Settings className="h-4 w-4 opacity-70" /><span className="flex-1">Admin</span><span className="h-6 w-6 rounded-full bg-sky-500 text-[10px] font-bold flex items-center justify-center">FN</span></div>}
          </div>
        </div>
      </div>
      <div className="bg-white border-r border-slate-200 flex flex-col min-h-0">
        <div className="flex items-center gap-2 px-3 h-11 border-b border-slate-200 text-[12px] text-slate-600">
          <input type="checkbox" readOnly className="h-3.5 w-3.5" /><span className="font-semibold text-slate-900">Toutes les machines</span><span>· 6</span>
          <span className="ml-auto flex items-center gap-1 rounded-md border border-slate-200 px-2 h-7">Trier : nom <ChevronDown className="h-3 w-3" /></span>
        </div>
        <div className="overflow-hidden">{PCS.map((x) => <DRow key={x.n} p={x} sel={x.n === selN} onPick={() => setSelN(x.n)} act={act} />)}</div>
      </div>
      <div className="p-5 overflow-hidden">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="flex gap-4 p-4">
            <div className="w-56 h-32 rounded-lg overflow-hidden bg-slate-800 relative shrink-0">
              <img src="/landing/access.jpg" alt="" className="w-full h-full object-cover opacity-90" />
              <span className="absolute bottom-2 left-2 text-[10px] bg-black/60 text-white rounded px-1.5">Aperçu · il y a 30 s</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-lg font-bold text-slate-900 flex items-center gap-2" data-testid="d-detail-name"><Dot on={p.on} />{p.n}</p>
              <p className="text-sm text-slate-500">{p.c} · {p.u} · {p.os === "mac" ? "macOS" : "Windows 11 Pro"}{!p.on && " · hors ligne"}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button disabled={!p.on} data-testid="d-connect-btn" className="rounded-md bg-sky-600 text-white text-sm font-semibold px-4 h-9 whitespace-nowrap flex items-center gap-1.5 disabled:bg-slate-300"><Plug className="h-4 w-4" />Se connecter</button>
                <button onClick={() => setTab("Messages")} data-testid="d-message-btn" className="rounded-md border border-slate-200 text-sm px-3 h-9 flex items-center gap-1.5 hover:bg-slate-50"><MessageSquare className="h-4 w-4" />Message</button>
                <button onClick={() => setTab("Commandes")} data-testid="d-command-btn" className="rounded-md border border-slate-200 text-sm px-3 h-9 flex items-center gap-1.5 hover:bg-slate-50"><Terminal className="h-4 w-4" />Commande</button>
                {p.on
                  ? <button onClick={() => act("restart", p)} data-testid="d-restart-btn" className="rounded-md border border-slate-200 text-sm px-3 h-9 flex items-center gap-1.5 hover:bg-slate-50"><RotateCcw className="h-4 w-4" />Redémarrer</button>
                  : <button onClick={() => act("wake", p)} data-testid="d-wake-btn" className="rounded-md border border-emerald-300 text-emerald-700 text-sm px-3 h-9 flex items-center gap-1.5 hover:bg-emerald-50"><Power className="h-4 w-4" />Réveiller (Wake-on-LAN)</button>}
              </div>
            </div>
          </div>
          <div className="flex gap-4 px-4 border-y border-slate-200 text-[13px] overflow-x-auto">
            {DTABS.map(([t, warn]) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`d-tab-${t}`} className={`py-2.5 border-b-2 whitespace-nowrap flex items-center gap-1 ${tab === t ? "border-sky-600 text-sky-700 font-semibold" : "border-transparent text-slate-500"}`}>
                {t}{warn && <span className="text-[10px] bg-red-500 text-white rounded-full px-1.5">{warn}</span>}
              </button>
            ))}
          </div>
          {tab === "Santé" && <DHealth />}
          {tab === "Mises à jour" && <DUpdates />}
          {tab === "Logiciels" && <DSoftware />}
          {tab === "Connexions" && <DConnections />}
          {tab === "Messages" && <DChat key={p.n} on={p.on} />}
          {tab === "Commandes" && <DCommands key={p.n} on={p.on} />}
          {tab === "Notes" && <DNotes key={p.n} />}
          {tab === "Infos" && <div className="grid grid-cols-[1fr_200px] gap-x-6 p-4 max-h-[390px] overflow-y-auto" data-testid="d-info">
            <div className="space-y-3">
              {INFO.map(([title, rows]) => (
                <div key={title}>
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">{title}</p>
                  <dl className="text-[12.5px] grid grid-cols-[150px_1fr] gap-y-1">
                    {rows.map(([k, v, cls]) => (
                      <div key={k} className="contents"><dt className="text-slate-500">{k}</dt><dd className={`text-slate-900 break-all ${cls || ""}`}>{v || <span className="text-slate-300">—</span>}</dd></div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
            <div className="space-y-3"><Gauge l="Processeur" v={12} /><Gauge l="Mémoire" v={58} /><Gauge l="Disque C:" v={41} /></div>
          </div>}
        </div>
      </div>
    </div>
  </Frame>
  );
};

// E — groups as horizontal tabs on top, machine list on the left, detail on the right.
const TabsTop = () => (
  <Frame>
    <div className="bg-white border-b border-slate-200 px-4 pt-3 flex items-end gap-1">
      {GROUPS.map((g, i) => (
        <span key={g.l} className={`px-3 pb-2.5 text-[13px] flex items-center gap-1.5 border-b-2 ${i === 0 ? "border-sky-600 text-sky-700 font-semibold" : "border-transparent text-slate-600"}`}>
          {g.l}<span className="text-[11px] bg-slate-100 rounded-full px-1.5">{g.n}</span>
        </span>
      ))}
      <div className="ml-auto pb-2 w-56"><Search1 /></div>
    </div>
    <div className="grid grid-cols-[420px_1fr] h-[calc(100%-49px)]">
      <div className="bg-white border-r border-slate-200 overflow-hidden">{PCS.map((p, i) => <Row key={p.n} p={p} sel={i === 0} />)}</div>
      <Detail />
    </div>
  </Frame>
);

// F — groups left, list on top, detail docked at the bottom (horizontal).
const DetailBottom = () => (
  <Frame>
    <div className="grid grid-cols-[220px_1fr] h-full">
      <div className="bg-white border-r border-slate-200"><div className="p-3"><Search1 /></div><GroupList /></div>
      <div className="grid grid-rows-[1fr_auto] min-h-0">
        <div className="bg-white overflow-hidden grid grid-cols-2">{PCS.map((p, i) => <div key={p.n} className="border-r border-slate-200"><Row p={p} sel={i === 0} dense /></div>)}</div>
        <div className="border-t-2 border-sky-600"><Detail horizontal /></div>
      </div>
    </div>
  </Frame>
);

// G — groups and machines in one tree (clients unfold their machines), detail on the right.
const Tree = () => {
  const clients = [...new Set(PCS.map((p) => p.c))];
  return (
    <Frame>
      <div className="grid grid-cols-[400px_1fr] h-full">
        <div className="bg-white border-r border-slate-200 overflow-hidden">
          <div className="p-3 border-b border-slate-200"><Search1 /></div>
          {GROUPS.slice(1, 4).map((g) => (
            <div key={g.l} className="flex items-center gap-2 px-4 py-2 text-[13px] text-slate-700"><g.i className="h-4 w-4 opacity-70" />{g.l}<span className="ml-auto text-[11px] bg-slate-200 rounded-full px-2">{g.n}</span></div>
          ))}
          {clients.map((c, ci) => (
            <div key={c}>
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-[13px] font-semibold border-y border-slate-200">
                {ci < 2 ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}<Users className="h-4 w-4 text-slate-500" />{c}
                <span className="ml-auto text-[11px] font-normal">{PCS.filter((p) => p.c === c && p.on).length}/{PCS.filter((p) => p.c === c).length}</span>
              </div>
              {ci < 2 && PCS.filter((p) => p.c === c).map((p) => (
                <div key={p.n} className={`flex items-center gap-2.5 pl-10 pr-4 py-1.5 text-[13px] ${p.n === "PC-ACCUEIL" ? "bg-sky-600 text-white" : ""}`}>
                  <Dot on={p.on} /><Red old={p.old} /><span className="flex-1 font-medium">{p.n}</span><span className="opacity-70 text-xs">{p.u}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <Detail />
      </div>
    </Frame>
  );
};

// H — groups left, list full width, detail slides in as a drawer over the list.
const Drawer = () => (
  <Frame>
    <div className="grid grid-cols-[220px_1fr] h-full relative">
      <div className="bg-white border-r border-slate-200"><div className="p-3"><Search1 /></div><GroupList compact /></div>
      <div className="bg-white overflow-hidden">
        <div className="grid grid-cols-[1.4fr_1fr_1fr_90px] px-4 py-2 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200"><span>Poste</span><span>Entreprise</span><span>Utilisateur</span><span>État</span></div>
        {PCS.map((p, i) => (
          <div key={p.n} className={`grid grid-cols-[1.4fr_1fr_1fr_90px] px-4 py-2.5 text-[13px] border-b border-slate-200 items-center ${i === 0 ? "bg-sky-50" : ""}`}>
            <span className="font-semibold flex items-center gap-2"><Os os={p.os} /><Red old={p.old} />{p.n}</span><span className="text-slate-600">{p.c}</span><span className="text-slate-600">{p.u}</span>
            <span className="flex items-center gap-1.5 text-xs"><Dot on={p.on} />{p.on ? "En ligne" : "Hors ligne"}</span>
          </div>
        ))}
      </div>
      <div className="absolute right-0 top-0 bottom-0 w-[400px] bg-white shadow-2xl border-l border-slate-200">
        <div className="flex items-center gap-4 px-5 pt-4 text-slate-500">
          <Info className="h-5 w-5 text-sky-600" /><Clock className="h-5 w-5" /><MessageSquare className="h-5 w-5" /><FileText className="h-5 w-5" /><X className="h-5 w-5 ml-auto" />
        </div>
        <Detail />
      </div>
    </div>
  </Frame>
);

const STYLES = [
  { id: "D", name: "Barre latérale sombre (v2)", desc: "Barre bleu nuit (Vues / Clients), liste avec cases et actions au survol, fiche avec aperçu d’écran, onglets et jauges.", C: SideDark },
  { id: "E", name: "Groupes en onglets", desc: "Les groupes deviennent des onglets en haut : plus de place pour la liste et la fiche, côte à côte.", C: TabsTop },
  { id: "F", name: "Fiche en bas", desc: "Groupes à gauche, postes sur 2 colonnes en haut, fiche du poste sélectionné en bandeau en bas.", C: DetailBottom },
  { id: "G", name: "Arbre clients", desc: "Groupes et postes dans une seule colonne : chaque client se déplie, la fiche à droite.", C: Tree },
  { id: "H", name: "Tableau + volet", desc: "Liste en tableau sur toute la largeur, la fiche glisse par-dessus à droite quand on choisit un poste.", C: Drawer },
];

export default function TemplatePreview() {
  const [cur, setCur] = useState("D");
  const S = STYLES.find((s) => s.id === cur);
  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-2"><LogoMark className="h-9 w-9" /><h1 className="text-3xl font-extrabold">Aperçus des modèles d'affichage</h1></div>
        <p className="text-slate-600 mb-6">Variantes proches de la structure actuelle (groupes, liste, fiche), avec une disposition différente. Données d'exemple.</p>
        <div className="grid grid-cols-5 gap-3 mb-6">
          {STYLES.map((s) => (
            <button key={s.id} onClick={() => setCur(s.id)} data-testid={`template-pick-${s.id}`}
              className={`text-left rounded-xl p-3 border-2 transition-colors ${cur === s.id ? "border-sky-600 bg-white" : "border-transparent bg-white/60 hover:bg-white"}`}>
              <p className="font-bold text-sm">{s.id} — {s.name}</p>
              <p className="text-xs text-slate-600 mt-1">{s.desc}</p>
            </button>
          ))}
        </div>
        <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-slate-200" data-testid={`template-preview-${cur}`}><S.C /></div>
      </div>
    </div>
  );
}
